/**
 * Web Research with Templates Tutorial - Beginner (Introduction to Templates)
 *
 * A beginner tutorial that introduces agent templates. The user creates a
 * workflow with a user prompt, then adds a run_agent step using the
 * Web Research Agent template instead of creating from scratch.
 */
import { TutorialStep } from '../../types/tutorial';

const TEMPLATE_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Web Research" workflow.';

/**
 * Specific values for the template tutorial.
 */
export const TEMPLATE_TUTORIAL_VALUES = {
  orchestration: {
    name: 'Web Research',
    description:
      'A workflow that uses a pre-built template agent to research topics from the web.',
  },
  userPrompt: {
    label: 'Get Research Topic',
    inputMessage: "Enter a topic and I'll research it for you!",
  },
};

/**
 * Template Tutorial Steps
 *
 * A beginner tutorial that introduces agent templates.
 * The user creates a workflow with a user prompt, then adds a run_agent
 * step using the Web Research Agent template instead of creating from scratch.
 *
 * Step Flow:
 * 1-3:   Navigate to Workflows, create workflow
 * 4-6:   Add User Prompt step
 * 7-10:  Add Run Agent step using template (click +, pick Agent - Create, use template, add step)
 * 11-13: Run, submit input, and view output
 */
export const TEMPLATE_TUTORIAL_STEPS: TutorialStep[] = [
  // --- Navigate to Workflows and create ---
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Welcome to Templates!',
    description:
      "In this tutorial, you'll learn how to use __agent templates__ to quickly add pre-built agents to your workflows.\n\n" +
      'Instead of creating an agent from scratch, templates give you a ready-made agent with instructions, tools, and configuration already set up.\n\n' +
      "You'll build a __Web Research__ workflow using the __Web Research Agent__ template.",
    actionPrompt: "Click 'Workflows' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Workflow',
    description: "Let's create a new workflow for our web research tool.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      'Set up the workflow details:\n\n' +
      '1. For Name, enter: **Web Research**\n\n' +
      '2. For Description, enter: **A workflow that uses a pre-built template agent to research topics from the web.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 2,
  },

  // --- Add User Prompt step ---
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add User Prompt',
    description:
      'Welcome to the workflow editor! First, we need a way for users to provide a research topic.\n\n' +
      'Click the __+__ button below the __Start__ node to add a step.',
    actionPrompt: 'Click the __+__ button below the Start node.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      'Select the __User Prompt__ step type. This will ask the user for a research topic.',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
    resumeFallbackStep: 4,
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      'Configure the User Prompt:\n\n' +
      '__Label__: **Get Research Topic**\n\n' +
      "__Input Message__: **Enter a topic and I'll research it for you!**",
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
    resumeFallbackStep: 4,
  },

  // --- Add Run Agent step using template ---
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the Research Agent',
    description:
      "Now we'll add a __Run Agent__ step. But instead of creating an agent from scratch, we'll use a __template__!\n\n" +
      'Templates are pre-built agents with instructions and tools already configured.',
    actionPrompt: 'Click the __+__ button below the User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The __Web Research Agent__ will __generate__ research findings from your topic, so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Use a Template',
    description:
      "The __Run Agent__ step type is already selected. Now let's create an agent from a template:\n\n" +
      '1. Click the __+__ button next to the agent dropdown\n' +
      '2. Select __Use Template__ from the menu\n' +
      '3. Find __Web Research Agent__ and click __Use Template__ on its card\n\n' +
      'The agent will be created automatically and selected in the dropdown - no setup needed!',
    actionPrompt:
      "Click __+__, select 'Use Template', then choose the Web Research Agent.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Step',
    description:
      'The __Web Research Agent__ is now selected in the agent dropdown.\n\n' +
      "Click __Add Step__ to add this agent step to your workflow. That's it - no need to write instructions or configure tools!",
    actionPrompt: "Click 'Add Step' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
    resumeFallbackStep: 7,
  },

  // --- Run, submit input, and view output ---
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Run Your Workflow!',
    description:
      'Your Web Research workflow is ready! When you run it, the workflow will:\n\n' +
      '• Ask for a research topic\n' +
      '• Send it to the Web Research Agent (created from template)\n' +
      '• Return comprehensive research findings',
    actionPrompt: "Click 'Execute' to run your workflow!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Submit Your Input',
    description:
      'The workflow is waiting for your input! Type a research topic and click __Submit__.\n\n' +
      'Try **renewable energy trends**, **history of jazz music**, or any topic you like!',
    actionPrompt: "Enter a topic and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
  },
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View Your Research!',
    description:
      'Your workflow is running! Once complete, check out __View Final Output__ to see the research results.\n\n' +
      'Notice how the Web Research Agent produced detailed results using its pre-configured tools and instructions - all from a template!\n\n' +
      "__What you've learned:__\n" +
      '• Browsing and selecting __agent templates__\n' +
      '• Creating agents from templates __directly in the workflow editor__\n' +
      '• How templates save time by providing __pre-configured agents__ with tools and instructions\n\n' +
      '__Tip:__ Need a research workflow scoped to a different topic? Open the Workflows page and ' +
      'click the __Duplicate__ icon on this row - the copy keeps the Web Research Agent wired in and ' +
      'is ready to retarget at a new topic.\n\n' +
      "Click __Finish Tutorial__ when you're ready!",
    actionPrompt: "View the output, then click 'Finish Tutorial'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: TEMPLATE_ROUTE_MSG,
  },
];
